import { z } from 'zod';
export const assetsWarehousesCapability = 'assets.warehouses.v1';
export const assetsWarehouseLocationIdSchema = z.string().uuid();
export const assetsWarehouseLocationTypeSchema = z.enum(['warehouse', 'bin']);
const nullableText = (maximum) => z.string().trim().max(maximum).nullable();
export const assetsWarehouseLocationInputSchema = z.strictObject({
    locationType: assetsWarehouseLocationTypeSchema,
    parentWarehouseId: assetsWarehouseLocationIdSchema.nullable(),
    code: z.string().trim().min(1).max(80),
    name: z.string().trim().min(1).max(200),
    addressLine1: nullableText(240),
    addressLine2: nullableText(240),
    city: nullableText(120),
    region: nullableText(120),
    postalCode: nullableText(40),
    countryCode: z.string().regex(/^[A-Z]{2}$/).nullable(),
    notes: nullableText(2_000),
}).superRefine((value, context) => {
    if (value.locationType === 'warehouse' && value.parentWarehouseId !== null) {
        context.addIssue({
            code: 'custom',
            path: ['parentWarehouseId'],
            message: 'A warehouse cannot have a parent warehouse.',
        });
    }
    if (value.locationType === 'bin' && value.parentWarehouseId === null) {
        context.addIssue({
            code: 'custom',
            path: ['parentWarehouseId'],
            message: 'A bin must belong to a warehouse.',
        });
    }
    if (value.locationType === 'bin' && [
        value.addressLine1,
        value.addressLine2,
        value.city,
        value.region,
        value.postalCode,
        value.countryCode,
    ].some((part) => part !== null)) {
        context.addIssue({
            code: 'custom',
            path: ['locationType'],
            message: 'Address fields belong to the warehouse; bins inherit that address.',
        });
    }
});
export const assetsWarehouseLocationSchema = z.strictObject({
    tenantId: z.string().uuid(),
    id: assetsWarehouseLocationIdSchema,
    location: assetsWarehouseLocationInputSchema,
    revision: z.number().int().positive(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    createdByMembershipId: z.string().uuid(),
    updatedByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const assetsWarehouseLocationVersionSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    locationId: assetsWarehouseLocationIdSchema,
    revision: z.number().int().positive(),
    operation: z.enum(['create', 'update', 'archive', 'restore']),
    location: assetsWarehouseLocationInputSchema,
    archivedAfter: z.boolean(),
    reason: z.string().trim().min(10).max(500),
    actorMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
});
export const assetsCreateWarehouseLocationCommandSchema = z.strictObject({
    kind: z.literal('create'),
    commandId: z.string().uuid(),
    locationId: assetsWarehouseLocationIdSchema,
    location: assetsWarehouseLocationInputSchema,
    reason: z.string().trim().min(10).max(500),
});
export const assetsUpdateWarehouseLocationCommandSchema = z.strictObject({
    kind: z.literal('update'),
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    location: assetsWarehouseLocationInputSchema,
    reason: z.string().trim().min(10).max(500),
});
export const assetsWarehouseLocationStateCommandSchema = z.strictObject({
    kind: z.enum(['archive', 'restore']),
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(10).max(500),
});
export const assetsWarehouseLocationCommandSchema = z.discriminatedUnion('kind', [
    assetsUpdateWarehouseLocationCommandSchema,
    assetsWarehouseLocationStateCommandSchema,
]);
export const assetsWarehouseLocationListQuerySchema = z.strictObject({
    query: z.string().trim().max(160).default(''),
    locationType: assetsWarehouseLocationTypeSchema.nullable().default(null),
    parentWarehouseId: assetsWarehouseLocationIdSchema.nullable().default(null),
    includeArchived: z.boolean().default(false),
    limit: z.number().int().min(1).max(500).default(100),
    offset: z.number().int().nonnegative().default(0),
});
const commandMetadataSchema = z.strictObject({
    commandId: z.string().uuid(),
    idempotent: z.boolean(),
});
export const assetsWarehouseLocationsResponseSchema = z.strictObject({
    data: z.array(assetsWarehouseLocationSchema),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    }),
});
export const assetsWarehouseLocationResponseSchema = z.strictObject({
    data: assetsWarehouseLocationSchema,
    command: commandMetadataSchema.optional(),
});
export const assetsWarehouseLocationVersionsResponseSchema = z.strictObject({
    data: z.array(assetsWarehouseLocationVersionSchema),
});
export function createAssetsWarehouseLocationDefaults(locationType = 'warehouse') {
    return {
        locationType,
        parentWarehouseId: null,
        code: '',
        name: '',
        addressLine1: null,
        addressLine2: null,
        city: null,
        region: null,
        postalCode: null,
        countryCode: locationType === 'warehouse' ? 'US' : null,
        notes: null,
    };
}
//# sourceMappingURL=assets-warehouse.contract.js.map